import React from "react";
import PropTypes from "prop-types";
import { onActivateKeyDown } from "../../../utils/keyboardActivate";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CircularProgress } from "@mui/material";
import {
  SubscriptionCard,
  // PaymentCardView,
  WarningBox,
  DiscountCodeComponent,
  UpcomingRefundComponent,
} from "../SharedComponents";
import {
  StyledDialog,
  DialogHeader,
  HeaderTitle,
  Title,
  CloseButton,
  Separator,
  ScrollableContent,
  CardsContainer,
  ChevronContainer,
  MiddleContentContainer,
  WarningContainer,
  ButtonsContainer,
  CancelText,
} from "../SubscriptionModal.styled";
import Button from "../../Button";
import { tokens } from "../../../utils";



/**
 * Modal component for downgrading subscriptions
 */
const DowngradeModal = ({
  open,
  onClose,
  onConfirm,
  fromPlan = {
    type: "premium",
    name: "Premium",
    price: "15",
    validityDate: "Sept 05, 2024",
  },
  toPlan = {
    type: "essential",
    name: "Essential",
    price: "5",
    validityDate: "Sept 06, 2024",
  },
  // showPaymentCard = true,
  warningItems = [],
  closeOnBackdropClick = false,
  loading = false,
  downgradeButtonText = "Downgrade",
  // handlePaymentMethodChange = ()=>{},
  isActionDisabled = false,
  // When true the modal is being reused for a full cancellation (to Free)
  // rather than a downgrade to a cheaper paid tier -- drives the header copy
  // so the dialog no longer says "Downgrade" during a cancel (SUB-010).
  isCancel = false,
  // Show the prorated-refund credit for the unused portion of the current
  // paid cycle (SUB-007). Parent passes true when the current plan is paid.
  showRefundInfo = false,
  // Promo code props
  promoCode = "",
  discountInfo = null,
  codeError = "",
  validatingCode = false,
  onApplyPromoCode = () => {},
}) => {
  // Check if downgrading to free plan
  const isDowngradingToFree = parseFloat(toPlan.price) === 0;
  // Header copy adapts to the action so a cancel no longer reads "Downgrade"
  // (SUB-010).
  const headerTitle = isCancel
    ? "Confirm Your Cancellation"
    : "Confirm Your Downgrade";
  // Check if the plan is annual subscription
  const isAnnualSubscription = toPlan.durationDisplay === "Annually";
  
  // Calculate the correct monthly price if discount applied to annual plan
  const getDisplayPrice = () => {
    if (!discountInfo) return toPlan.price;
    
    // If it's an annual plan, divide by 12 to get the monthly price
    if (isAnnualSubscription) {
      return (parseFloat(discountInfo.discountedPrice) / 12).toFixed(2);
    }
    
    return discountInfo.discountedPrice;
  };
  
  // Same for original price if needed
  const getDisplayOriginalPrice = () => {
    if (!discountInfo) return null;
    
    if (isAnnualSubscription) {
      return (parseFloat(discountInfo.originalPrice) / 12).toFixed(2);
    }
    
    return discountInfo.originalPrice;
  };

  const handleBackdropClick = (event) => {
    // Only close if closeOnBackdropClick is true
    if (closeOnBackdropClick) {
      onClose(event);
    }
  };


  // For onConfirm, we should pass the promo code if it's valid
  const handleConfirm = () => {
    onConfirm({
      promoCode: discountInfo ? discountInfo.code : null
    });
  };

  const renderMiddleContent = () => {
    const components = [];

    // Only show discount code component if NOT downgrading to free
    if (!isDowngradingToFree) {
      components.push(
        <DiscountCodeComponent
          key="discount"
          onApplyCode={onApplyPromoCode}
          initialCode={promoCode}
          placeholder="Add Coupon here"
          showTitle={true}
          error={codeError}
          loading={validatingCode}
          discountInfo={discountInfo}
        />
      );
    }

    // Show the prorated-refund credit for the unused portion of the current
    // paid billing cycle so a paying user downgrading/cancelling sees the
    // credit they are owed instead of nothing (SUB-007). The component fetches
    // its own refund figure and renders its own loading/error states.
    if (showRefundInfo) {
      components.push(<UpcomingRefundComponent key="refund" />);
    }

    // Comment out payment component
    /* if (showPaymentCard) {
      components.push(
        <PaymentCardView
          key="payment"
          onPaymentMethodChanged={handlePaymentMethodChange}
        />
      );
    } */

    return components.length > 0 ? components : null;
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      // a11y-i18n-mobileweb:I3/E-6 — Esc-to-close and backdrop-click-to-close
      // are separate UX conventions; coupling them via `!closeOnBackdropClick`
      // meant Esc was ALSO disabled whenever backdrop-click was (the default),
      // trapping a keyboard user on a payment-adjacent modal with no
      // keyboard-reachable dismiss. Esc now always closes — the confirm CTA
      // still requires an explicit click/Enter on the "Downgrade" button, so
      // nothing about the plan-change itself becomes accidental.
      disableEscapeKeyDown={false}
      onClick={(e) => e.stopPropagation()}
      onBackdropClick={handleBackdropClick}
    >
      <DialogHeader>
        <HeaderTitle>
          <WarningAmberIcon style={{ color: "#F79A00" }} />
          <Title>{headerTitle}</Title>
        </HeaderTitle>
        {/* a11y-i18n-mobileweb:I3/E-6 — CloseButton is a styled <div>; a
            div/span with only onClick is not in the Tab order and has no
            Enter/Space activation. role="button" + tabIndex + onKeyDown is
            the ARIA APG pattern for a non-native activation control. */}
        <CloseButton
          role="button"
          tabIndex={0}
          aria-label="Close"
          onClick={onClose}
          onKeyDown={onActivateKeyDown(onClose)}
        >
          <CloseIcon />
        </CloseButton>
      </DialogHeader>

      <Separator />

      <ScrollableContent>
        <CardsContainer>
          <SubscriptionCard
            planType={fromPlan.type}
            planName={fromPlan.name}
            price={fromPlan.price}
            interval="/month"
            validityLabel="Until"
            validityDate={fromPlan.validityDate}
            isActive={true}
            durationDisplay={fromPlan.durationDisplay}
          />

          <ChevronContainer>
            <ChevronRightIcon style={{ color: "#9B5AF8", fontSize: 40 }} />
          </ChevronContainer>

          <SubscriptionCard
            planType={toPlan.type}
            planName={toPlan.name}
            price={getDisplayPrice()}
            originalPrice={getDisplayOriginalPrice()}
            interval="/month"
            validityLabel="Starting from"
            validityDate={toPlan.validityDate}
            durationDisplay={toPlan.durationDisplay}
            yearlyPrice={discountInfo ? discountInfo.discountedPrice : toPlan.yearlyPrice}
          />
        </CardsContainer>

        {renderMiddleContent() && (
          <MiddleContentContainer>
            {renderMiddleContent()}
          </MiddleContentContainer>
        )}

        <WarningContainer>
          <WarningBox isUpgrade={false} warningItems={warningItems} />
        </WarningContainer>
      </ScrollableContent>

      <ButtonsContainer>
        <CancelText
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={onActivateKeyDown(onClose)}
        >
          Cancel
        </CancelText>
        <Button
          width={"auto"}
          // DL-030 (ISP-4018): filled confirm CTA = design-system ON-FILL purple
          // #8F38EF (white 5.28:1 AA) not accent #9B5AF8 (4.03:1). Enabled-only so
          // the disabled/loading grey .disabled fill is preserved.
          style={{
            backgroundColor:
              loading || isActionDisabled ? undefined : tokens.brand.primaryActive,
          }}
          onClick={handleConfirm}
          disabled={loading || isActionDisabled}
        >
          {loading ? (
            <CircularProgress size={20} style={{ color: "white" }} />
          ) : (
            downgradeButtonText
          )}
        </Button>
      </ButtonsContainer>
    </StyledDialog>
  );
};

DowngradeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  fromPlan: PropTypes.shape({
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    validityDate: PropTypes.string.isRequired,
    isAnnual: PropTypes.bool,
    monthlyPrice: PropTypes.string,
    yearlyPrice: PropTypes.string,
  }),
  toPlan: PropTypes.shape({
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    validityDate: PropTypes.string.isRequired,
    price_id: PropTypes.string.isRequired,
    product_id: PropTypes.string.isRequired,
    isAnnual: PropTypes.bool,
    monthlyPrice: PropTypes.string,
    yearlyPrice: PropTypes.string,
  }),
  // showPaymentCard: PropTypes.bool,
  warningItems: PropTypes.arrayOf(PropTypes.string),
  closeOnBackdropClick: PropTypes.bool,
  loading: PropTypes.bool,
  downgradeButtonText: PropTypes.string,
  // handlePaymentMethodChange: PropTypes.func,
  isActionDisabled: PropTypes.bool,
  isCancel: PropTypes.bool,
  showRefundInfo: PropTypes.bool,
  // Promo code props
  promoCode: PropTypes.string,
  discountInfo: PropTypes.object,
  codeError: PropTypes.string,
  validatingCode: PropTypes.bool,
  onApplyPromoCode: PropTypes.func,
};

export default DowngradeModal;
